import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import PlatformOwnerAppShell from '../components/PlatformOwnerAppShell';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { PreviewSecurityBanner } from '../components/StepUpAuthModal';
import {
  Shield, AlertTriangle, CheckCircle, XCircle, Clock,
  Search, Download, RefreshCw, ChevronDown, ChevronUp,
  LogIn, LogOut, Key, Lock, UserCog, ArrowRightLeft, Eye
} from 'lucide-react';

const EVENT_ICONS = {
  login_success: { icon: LogIn, color: 'text-green-600', bg: 'bg-green-50' },
  login_failure: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50' },
  login_locked_out: { icon: Lock, color: 'text-red-700', bg: 'bg-red-100' },
  mfa_setup_started: { icon: Key, color: 'text-blue-500', bg: 'bg-blue-50' },
  mfa_setup_completed: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
  mfa_disabled: { icon: XCircle, color: 'text-amber-600', bg: 'bg-amber-50' },
  mfa_success: { icon: CheckCircle, color: 'text-green-600', bg: 'bg-green-50' },
  mfa_failure: { icon: XCircle, color: 'text-red-500', bg: 'bg-red-50' },
  mfa_recovery_code_used: { icon: Key, color: 'text-amber-600', bg: 'bg-amber-50' },
  step_up_success: { icon: Shield, color: 'text-green-600', bg: 'bg-green-50' },
  step_up_failure: { icon: Shield, color: 'text-red-500', bg: 'bg-red-50' },
  super_admin_granted: { icon: UserCog, color: 'text-purple-600', bg: 'bg-purple-50' },
  super_admin_revoked: { icon: UserCog, color: 'text-red-500', bg: 'bg-red-50' },
  admin_granted: { icon: UserCog, color: 'text-blue-600', bg: 'bg-blue-50' },
  admin_revoked: { icon: UserCog, color: 'text-red-500', bg: 'bg-red-50' },
  ownership_transfer_started: { icon: ArrowRightLeft, color: 'text-amber-600', bg: 'bg-amber-50' },
  ownership_transfer_completed: { icon: ArrowRightLeft, color: 'text-green-600', bg: 'bg-green-50' },
  ownership_transfer_failed: { icon: ArrowRightLeft, color: 'text-red-500', bg: 'bg-red-50' },
  user_deleted: { icon: XCircle, color: 'text-red-600', bg: 'bg-red-50' },
  user_disabled: { icon: Lock, color: 'text-amber-600', bg: 'bg-amber-50' },
  security_activity_exported: { icon: Download, color: 'text-slate-500', bg: 'bg-slate-50' },
};

const DEFAULT_ICON = { icon: Shield, color: 'text-slate-500', bg: 'bg-slate-50' };

function formatEventLabel(action) {
  return (action || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function formatTimestamp(ts) {
  if (!ts) return '-';
  try {
    const d = new Date(ts);
    return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) +
      ' ' + d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
  } catch { return ts; }
}

function isFailure(action) {
  return /failure|failed|locked|revoked|disabled/.test(action || '');
}

function isSuccess(action) {
  return /success|completed|granted|enabled|setup_completed/.test(action || '');
}

function SummaryCard({ label, value, icon: Icon, color = 'text-slate-700', bgColor = 'bg-white' }) {
  return (
    <div className={`${bgColor} rounded-xl border p-4 flex items-center gap-3`}>
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${color} bg-current/10`}>
        <Icon className="w-5 h-5" />
      </div>
      <div>
        <p className="text-2xl font-bold text-slate-900">{value}</p>
        <p className="text-xs text-slate-500">{label}</p>
      </div>
    </div>
  );
}

function EventRow({ event, expanded, onToggle }) {
  const cfg = EVENT_ICONS[event.action] || DEFAULT_ICON;
  const IconComp = cfg.icon;
  const fail = isFailure(event.action);
  const details = event.details || {};

  return (
    <div className={`border-b last:border-b-0 ${fail ? 'bg-red-50/30' : ''}`} data-testid={`event-row-${event.log_id}`}>
      <button
        className="w-full px-4 py-3 flex items-center gap-3 text-left hover:bg-slate-50/50 transition-colors"
        onClick={onToggle}
      >
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${cfg.bg}`}>
          <IconComp className={`w-4 h-4 ${cfg.color}`} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-slate-800 truncate">{formatEventLabel(event.action)}</p>
          <p className="text-xs text-slate-500 truncate">{event.user_email || '-'}</p>
        </div>
        <div className="text-right shrink-0 flex items-center gap-2">
          <span className="text-[11px] text-slate-400 hidden sm:block">{formatTimestamp(event.created_at)}</span>
          {fail ? (
            <Badge className="bg-red-100 text-red-700 text-[10px]">Fail</Badge>
          ) : isSuccess(event.action) ? (
            <Badge className="bg-green-100 text-green-700 text-[10px]">OK</Badge>
          ) : (
            <Badge variant="outline" className="text-[10px]">Info</Badge>
          )}
          {expanded ? <ChevronUp className="w-4 h-4 text-slate-400" /> : <ChevronDown className="w-4 h-4 text-slate-400" />}
        </div>
      </button>
      {expanded && (
        <div className="px-4 pb-3 pl-16 space-y-1 text-xs text-slate-600" data-testid={`event-detail-${event.log_id}`}>
          <p><span className="text-slate-400">Timestamp:</span> {formatTimestamp(event.created_at)}</p>
          <p><span className="text-slate-400">Actor:</span> {event.user_email} ({event.user_id})</p>
          <p><span className="text-slate-400">Event:</span> {event.action}</p>
          {details.ip && <p><span className="text-slate-400">IP:</span> {details.ip}</p>}
          {details.reason && <p><span className="text-slate-400">Reason:</span> {details.reason}</p>}
          {details.target_email && <p><span className="text-slate-400">Target:</span> {details.target_email}</p>}
          {details.target_user && <p><span className="text-slate-400">Target User:</span> {details.target_user}</p>}
          {details.new_owner_email && <p><span className="text-slate-400">New Owner:</span> {details.new_owner_email}</p>}
          {details.remaining_codes !== undefined && <p><span className="text-slate-400">Recovery Codes Left:</span> {details.remaining_codes}</p>}
          {details.email_attempts !== undefined && <p><span className="text-slate-400">Email Attempts:</span> {details.email_attempts}</p>}
        </div>
      )}
    </div>
  );
}

export default function SecurityActivity() {
  const { api } = useAuth();
  const [events, setEvents] = useState([]);
  const [total, setTotal] = useState(0);
  const [summary, setSummary] = useState(null);
  const [signals, setSignals] = useState([]);
  const [eventTypes, setEventTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  // Filters
  const [filterType, setFilterType] = useState('');
  const [filterEmail, setFilterEmail] = useState('');
  const [filterOutcome, setFilterOutcome] = useState('');
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('limit', String(PAGE_SIZE));
      params.append('skip', String(page * PAGE_SIZE));
      if (filterType) params.append('event_type', filterType);
      if (filterEmail) params.append('user_email', filterEmail);
      if (filterOutcome) params.append('outcome', filterOutcome);
      const res = await api.get(`/platform-owner/security-activity?${params.toString()}`);
      setEvents(res.data.events || []);
      setTotal(res.data.total || 0);
      setSummary(res.data.summary || null);
      setSignals(res.data.suspicious_signals || []);
      setEventTypes(res.data.event_types || []);
    } catch {
      toast.error('Failed to load security activity');
    }
    setLoading(false);
  }, [api, filterType, filterEmail, filterOutcome, page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      if (filterType) params.append('event_type', filterType);
      const res = await api.get(`/platform-owner/security-activity/export?${params.toString()}`);
      const blob = new Blob([JSON.stringify(res.data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `security-activity-${new Date().toISOString().slice(0, 10)}.json`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`Exported ${res.data.total} events`);
    } catch {
      toast.error('Export failed');
    }
  };

  const totalPages = Math.ceil(total / PAGE_SIZE);

  return (
    <PlatformOwnerAppShell activeId="security-activity" title="Security Activity">
      <div className="space-y-4" data-testid="security-activity-page">

        <PreviewSecurityBanner />

        {/* Suspicious Signals */}
        {signals.length > 0 && (
          <div className="space-y-2" data-testid="suspicious-signals">
            {signals.map((sig, i) => (
              <div
                key={i}
                className={`flex items-start gap-3 p-3 rounded-xl border ${
                  sig.severity === 'high' ? 'bg-red-50 border-red-200' : 'bg-amber-50 border-amber-200'
                }`}
                data-testid={`signal-${sig.type}`}
              >
                <AlertTriangle className={`w-4 h-4 shrink-0 mt-0.5 ${sig.severity === 'high' ? 'text-red-600' : 'text-amber-600'}`} />
                <div>
                  <p className={`text-sm font-medium ${sig.severity === 'high' ? 'text-red-800' : 'text-amber-800'}`}>
                    {sig.type === 'repeated_login_failures' ? 'Brute Force Suspect' :
                     sig.type === 'repeated_mfa_failures' ? 'MFA Attack Suspect' :
                     sig.type === 'repeated_step_up_failures' ? 'Step-Up Auth Failures' :
                     sig.type === 'account_lockouts' ? 'Account Lockouts' : sig.type}
                  </p>
                  <p className="text-xs text-slate-600 mt-0.5">{sig.message}</p>
                </div>
                <Badge className={`shrink-0 text-[10px] ${sig.severity === 'high' ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800'}`}>
                  {sig.severity}
                </Badge>
              </div>
            ))}
          </div>
        )}

        {/* Summary Stats */}
        {summary && (
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="summary-stats">
            <SummaryCard label="Failed Logins (24h)" value={summary.failed_logins} icon={XCircle} color="text-red-500" />
            <SummaryCard label="Successful Logins" value={summary.successful_logins} icon={LogIn} color="text-green-600" />
            <SummaryCard label="MFA Failures" value={summary.mfa_failures} icon={Key} color="text-amber-500" />
            <SummaryCard label="Step-Up Failures" value={summary.step_up_failures} icon={Shield} color="text-orange-500" />
            <SummaryCard label="Lockouts" value={summary.lockouts} icon={Lock} color="text-red-700" />
            <SummaryCard label="Admin Actions" value={summary.admin_actions} icon={UserCog} color="text-purple-600" />
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-3">
            <div className="flex flex-col sm:flex-row gap-2 items-stretch sm:items-center">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Filter by email..."
                  value={filterEmail}
                  onChange={e => { setFilterEmail(e.target.value); setPage(0); }}
                  className="pl-9 text-sm h-9"
                  data-testid="filter-email-input"
                />
              </div>
              <select
                value={filterType}
                onChange={e => { setFilterType(e.target.value); setPage(0); }}
                className="h-9 rounded-lg border border-slate-200 text-sm px-3 bg-white text-slate-700"
                data-testid="filter-type-select"
              >
                <option value="">All Event Types</option>
                {eventTypes.map(t => (
                  <option key={t} value={t}>{formatEventLabel(t)}</option>
                ))}
              </select>
              <select
                value={filterOutcome}
                onChange={e => { setFilterOutcome(e.target.value); setPage(0); }}
                className="h-9 rounded-lg border border-slate-200 text-sm px-3 bg-white text-slate-700"
                data-testid="filter-outcome-select"
              >
                <option value="">All Outcomes</option>
                <option value="success">Success</option>
                <option value="failure">Failure</option>
              </select>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={fetchData} className="h-9" data-testid="refresh-btn">
                  <RefreshCw className="w-4 h-4" />
                </Button>
                <Button variant="outline" size="sm" onClick={handleExport} className="h-9" data-testid="export-btn">
                  <Download className="w-4 h-4 mr-1" /> Export
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Events Timeline */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#004B8D]" />
                Security Timeline
              </CardTitle>
              <span className="text-xs text-slate-400">{total} event{total !== 1 ? 's' : ''}</span>
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="p-6 space-y-3">
                {[1, 2, 3, 4, 5].map(i => <div key={i} className="h-14 bg-slate-50 rounded-lg animate-pulse" />)}
              </div>
            ) : events.length === 0 ? (
              <div className="p-12 text-center text-slate-400" data-testid="empty-state">
                <Shield className="w-10 h-10 mx-auto mb-3 opacity-50" />
                <p className="font-medium">No security events found</p>
                <p className="text-sm mt-1">Adjust your filters or check back later.</p>
              </div>
            ) : (
              <div data-testid="events-list">
                {events.map(ev => (
                  <EventRow
                    key={ev.log_id}
                    event={ev}
                    expanded={expandedId === ev.log_id}
                    onToggle={() => setExpandedId(expandedId === ev.log_id ? null : ev.log_id)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between text-sm">
            <Button variant="outline" size="sm" disabled={page === 0} onClick={() => setPage(p => p - 1)}>
              Previous
            </Button>
            <span className="text-slate-500">Page {page + 1} of {totalPages}</span>
            <Button variant="outline" size="sm" disabled={page >= totalPages - 1} onClick={() => setPage(p => p + 1)}>
              Next
            </Button>
          </div>
        )}
      </div>
    </PlatformOwnerAppShell>
  );
}
